/**
 * PageHeader.tsx
 *
 * Title, optional subtitle and breadcrumbs, and a cluster of actions. 144 files use it.
 *
 * ── Why the action cluster used to overflow ──────────────────────────────────────
 * It was `flex-shrink-0 flex-nowrap`, and the shared button is itself `shrink-0
 * whitespace-nowrap`, so nothing in the row could give: three or four actions simply
 * pushed past the viewport. The title was no help either — in the compact variant it
 * is `truncate` inside `min-w-0`, so it would shrink to an ellipsis and then to nothing
 * while the buttons kept their full width.
 *
 * Below `md` the title and actions become separate rows and the cluster is allowed to
 * wrap. At `md` and above every one of those base utilities is overridden back to
 * today's value, so the desktop row is unchanged — including during print, which lays
 * out near 816px and therefore takes the `md:` branch.
 *
 * Actions are typed as a node and call sites pass arbitrary JSX, so there is no typed
 * action list to collapse into a menu without editing all 144 of them. Wrapping is the
 * change that costs no call-site churn.
 */
import React from 'react';
import { cn } from '@/lib/utils';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import type { BreadcrumbItem } from '@/types/common.types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PageHeaderProps {
  title: string;
  subtitle?: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  actions?: React.ReactNode;
  className?: string;
  variant?: 'default' | 'compact';
}

// ─── Styles ───────────────────────────────────────────────────────────────────

/**
 * Shared by both variants, because both overflowed the same way. Every base utility
 * here has an `md:` counterpart restoring the previous value: wrap becomes nowrap, and
 * the cluster becomes unshrinkable again.
 */
const ACTIONS_CLASS =
  'flex flex-wrap items-center gap-2 md:flex-shrink-0 md:flex-nowrap';

// ─── Component ────────────────────────────────────────────────────────────────

export function PageHeader({
  title,
  subtitle,
  breadcrumbs,
  actions,
  className,
  variant = 'default',
}: PageHeaderProps) {
  if (variant === 'compact') {
    return (
      <div
        className={cn(
          'mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4',
          className,
        )}
      >
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold leading-tight text-slate-900">{title}</h1>
        </div>

        {actions && <div className={ACTIONS_CLASS}>{actions}</div>}
      </div>
    );
  }

  return (
    <div className={cn('mb-6', className)}>
      {/* Breadcrumbs row */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs items={breadcrumbs} className="mb-2" />
      )}

      {/* Title row */}
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 leading-tight">{title}</h1>
          {subtitle && (
            <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>
          )}
        </div>

        {actions && <div className={ACTIONS_CLASS}>{actions}</div>}
      </div>
    </div>
  );
}
